import { z } from 'zod';
import { PublicCheckoutStatusSchema, PublicExperienceProviderSchema, PublicExperienceSchemaVersionSchema, PublicExperienceSchemaVersionV2Schema, SubmissionWorkflowStatusEnum, SubmissionWorkflowStatusSchema, } from './enums';
import { PublicExperienceContactSchema } from './contact';
import { ProviderExecutionScopeSchema } from './execution-scope';
import { AcceptedOfferSnapshotSchema } from './offer';
import { BusinessIdSchema, CheckoutIdSchema, ExperienceIdSchema, IsoTimestampSchema, StripeAccountIdSchema, StripeCustomerIdSchema, StripePriceIdSchema, StripeSubscriptionIdSchema, SubmissionIdSchema, } from './primitives';
import { LiveRecurringSummarySchema } from './billing';
/**
 * Public Experiences — the form submission (the business record).
 *
 * A submission is the local workflow artefact: who submitted what and what they
 * accepted. It is completed exactly once, by the first server-verified payment.
 * Later recurring invoices are Stripe billing history and must never recreate,
 * duplicate or reopen it.
 */
/**
 * Local workflow state machine, mirroring the checkout's terminal outcome.
 *
 *   PENDING   -> CONFIRMED | ABANDONED
 *   CONFIRMED -> (terminal; a later failed invoice does NOT move it back)
 *   ABANDONED -> CONFIRMED  (late `invoice.paid` after an expiry race)
 */
export const SUBMISSION_WORKFLOW_TRANSITIONS = {
    [SubmissionWorkflowStatusEnum.PENDING]: [
        SubmissionWorkflowStatusEnum.BILLING_SCHEDULED,
        SubmissionWorkflowStatusEnum.CONFIRMED,
        SubmissionWorkflowStatusEnum.ABANDONED,
    ],
    [SubmissionWorkflowStatusEnum.CONFIRMED]: [],
    [SubmissionWorkflowStatusEnum.BILLING_SCHEDULED]: [
        SubmissionWorkflowStatusEnum.CONFIRMED,
    ],
    [SubmissionWorkflowStatusEnum.ABANDONED]: [
        SubmissionWorkflowStatusEnum.CONFIRMED,
    ],
};
export const canTransitionSubmissionWorkflow = (from, to) => SUBMISSION_WORKFLOW_TRANSITIONS[from].includes(to);
/**
 * Acceptance evidence. Captured server-side at submit time and never editable.
 * `ipAddress`/`userAgent` are retained as consent evidence only.
 */
export const SubmissionAcceptanceSchema = z
    .object({
    offerSnapshot: AcceptedOfferSnapshotSchema,
    acceptedAt: IsoTimestampSchema,
    // For Jotform-managed terms, acceptedAt records the offer snapshot time,
    // not a verified checkbox acceptance. No consent evidence is asserted.
    termsHandling: z.literal('JOTFORM_MANAGED').optional(),
    ipAddress: z.string().max(64).nullable().default(null),
    userAgent: z.string().max(512).nullable().default(null),
})
    .strict();
/**
 * Minimal provider linkage kept on the submission so the live billing summary can
 * be fetched later, plus the `priceId` the subscription was actually created
 * against.
 *
 * `priceId` is recorded here (and on the checkout document) so a renewal billed
 * on an older price can still be reconciled after the experience is republished
 * at a new amount: history never depends on the experience's current config. It
 * pairs with the canonical `accepted.offerSnapshot` money fields
 * (`amountCents`, `currency`, `interval`, `intervalCount`).
 *
 * It is deliberately NOT part of `AcceptedOfferSnapshotSchema`: that snapshot is
 * evidence of what the CUSTOMER agreed to and carries no provider identifiers.
 */
export const SubmissionProviderLinkV1Schema = z
    .object({
    provider: PublicExperienceProviderSchema,
    accountId: StripeAccountIdSchema,
    /** The Price the subscription was created against, at creation time. */
    priceId: StripePriceIdSchema,
    customerId: StripeCustomerIdSchema.nullable().default(null),
    subscriptionId: StripeSubscriptionIdSchema.nullable().default(null),
})
    .strict();
/**
 * Contract 2.0.0 linkage: v1 fields (v1 meaning preserved) plus the REQUIRED
 * `executionScope` frozen from the checkout, so a historical billing read or
 * cancel always addresses the account that owns the Subscription.
 */
export const SubmissionProviderLinkSchema = SubmissionProviderLinkV1Schema.extend({
    executionScope: ProviderExecutionScopeSchema,
}).strict();
/** Reader-side union for admin/merchant detail views of any submission. */
export const AnySubmissionProviderLinkSchema = z.union([
    SubmissionProviderLinkSchema,
    SubmissionProviderLinkV1Schema,
]);
const SubmissionDocumentBaseShape = {
    submissionId: SubmissionIdSchema,
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    checkoutId: CheckoutIdSchema,
    contact: PublicExperienceContactSchema,
    accepted: SubmissionAcceptanceSchema,
    workflowStatus: SubmissionWorkflowStatusSchema,
    confirmedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
};
/**
 * Private submission document
 * (`/publicExperiencePrivate/{businessId}/experiences/{experienceId}/
 * pxSubmissions/{submissionId}`). Server-only; holds the contact PII.
 *
 * RETENTION: this is a durable BUSINESS RECORD (who signed up, what they agreed
 * to, when). It has **no automatic expiration, no TTL and no cleanup job** — in
 * MVP1 or planned — and must not be conflated with the short-lived
 * checkout/security artifacts in `pxCheckouts`, which carry a separate,
 * not-yet-implemented 90-day target retention.
 *
 * `…V1Schema` is the contract 1.0.0 shape (platform-owned destination record),
 * retained verbatim; v1 documents are never rewritten.
 */
export const PublicExperienceSubmissionDocumentV1Schema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionSchema,
    ...SubmissionDocumentBaseShape,
    provider: SubmissionProviderLinkV1Schema,
})
    .strict();
/** Contract 2.0.0 submission: `schemaVersion: 2`, scope-bearing linkage. */
export const PublicExperienceSubmissionDocumentSchema = z
    .object({
    schemaVersion: PublicExperienceSchemaVersionV2Schema,
    ...SubmissionDocumentBaseShape,
    provider: SubmissionProviderLinkSchema,
})
    .strict();
/** Discriminated on `schemaVersion` — the only legacy discriminator. */
export const AnyPublicExperienceSubmissionDocumentSchema = z.discriminatedUnion('schemaVersion', [
    PublicExperienceSubmissionDocumentV1Schema,
    PublicExperienceSubmissionDocumentSchema,
]);
/**
 * Admin submission list row. Contains contact data (the admin is authorized) but
 * no provider identifiers and no live billing read — the list must not fan out to
 * the provider once per row.
 */
export const AdminSubmissionSummarySchema = z
    .object({
    submissionId: SubmissionIdSchema,
    checkoutId: CheckoutIdSchema,
    contact: PublicExperienceContactSchema,
    workflowStatus: SubmissionWorkflowStatusSchema,
    checkoutStatus: PublicCheckoutStatusSchema,
    amountCents: z.number().int().min(1),
    currency: z.literal('USD'),
    offerRevision: z.number().int().min(1),
    hasSubscription: z.boolean(),
    createdAt: IsoTimestampSchema,
    confirmedAt: IsoTimestampSchema.nullable(),
})
    .strict();
/**
 * Admin submission detail. `billing` is the live provider read and is a
 * discriminated union so "we could not reach the provider" is never confused
 * with "the subscription is inactive".
 */
export const AdminSubmissionDetailSchema = z
    .object({
    submissionId: SubmissionIdSchema,
    businessId: BusinessIdSchema,
    experienceId: ExperienceIdSchema,
    checkoutId: CheckoutIdSchema,
    contact: PublicExperienceContactSchema,
    accepted: SubmissionAcceptanceSchema,
    workflowStatus: SubmissionWorkflowStatusSchema,
    checkoutStatus: PublicCheckoutStatusSchema,
    confirmedAt: IsoTimestampSchema.nullable(),
    /** v2 (scope-bearing) or v1 linkage, depending on when the signup happened. */
    provider: AnySubmissionProviderLinkSchema,
    billing: LiveRecurringSummarySchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict();
